'use client'

import { forwardRef } from 'react'
import {
  Root,
  Trigger,
  Portal,
  Close,
  Overlay,
  Content,
  Title,
  Description,
} from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { OVERLAY_MOTION_CLASS } from '@/components/ui/motion'
const Dialog = Root
const DialogTrigger = Trigger
const DialogPortal = Portal
const DialogClose = Close
const DialogOverlay = forwardRef(({ className, ...props }, ref) => (
  <Overlay
    ref={ref}
    className={cn(OVERLAY_MOTION_CLASS, 'fixed inset-0 z-[999] bg-black/80', className)}
    {...props}
  />
))
DialogOverlay.displayName = Overlay.displayName
const DialogCloseVisual = () => (
  <span className="relative block">
    <span className="absolute inset-0 bg-accent rounded-sm opacity-0 group-focus/dialog-close:opacity-100 data-[state=open]:opacity-100 transition-opacity duration-150 pointer-events-none" />
    <span className="absolute -inset-1 rounded-md border-2 border-primary opacity-0 group-focus/dialog-close:opacity-100 transition-opacity duration-150 pointer-events-none" />
    <span className="relative z-10">
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </span>
  </span>
)
const DialogContent = forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-[999] grid w-[calc(100vw-1rem)] max-w-lg max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border border-border/40 bg-background p-6 shadow-lg duration-200 ease-out-quint [will-change:opacity,transform] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-xl overflow-x-hidden overflow-y-auto overscroll-contain',
        className,
      )}
      {...props}
    >
      {children}
      <Close className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:text-muted-foreground group/dialog-close">
        <DialogCloseVisual />
      </Close>
    </Content>
  </DialogPortal>
))
DialogContent.displayName = Content.displayName
const DialogHeader = ({ className, ...props }) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'
const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'
const DialogTitle = forwardRef(({ className, ...props }, ref) => (
  <Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
DialogTitle.displayName = Title.displayName
const DialogDescription = forwardRef(({ className, ...props }, ref) => (
  <Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))
DialogDescription.displayName = Description.displayName
export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
